import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

/**
 * The brand guide's dialog anatomy: Djupet header with a 2px Droppen seam, light work area,
 * footer bar with the action on the right.
 *
 * Reserved for the irreversible. Anything undoable gets an undo toast instead of a confirmation
 * dialog — see components/toast.tsx.
 */
export function Dialog({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
  wide = false,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** For content that needs room, like the PDF viewer. */
  wide?: boolean;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus management runs ONLY when the dialog opens or closes. It must not depend on onClose:
  // callers pass inline arrows that change identity on every keystroke inside the dialog, and a
  // re-running effect would yank focus from the field after each character.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Fields inside the dialog (autoFocus) keep their focus; the panel is only a fallback so
    // Escape works immediately.
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }
    // Return focus where it came from, so closing does not dump the user at the top of the page.
    return () => previouslyFocused?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      onMouseDown={(event) => event.target === overlayRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-start justify-center p-s7 pt-[12vh]"
      style={{ background: "color-mix(in srgb, var(--deep) 45%, transparent)" }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`nb-animate-panel ${
          wide ? "w-[min(920px,100%)]" : "w-[min(520px,100%)]"
        } overflow-hidden rounded-card bg-surface-1 shadow-dialog`}
      >
        <header className="nb-chrome px-s6 py-s5">
          <h2 className="text-title font-bold">{title}</h2>
          {subtitle && <p className="mt-s1 text-overline uppercase text-drop">{subtitle}</p>}
        </header>
        <div className="p-s6 text-ink">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-s4 border-t border-line bg-surface-2 px-s6 py-s5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
